declare global {
  interface Window {
    dataLayer: Array<Record<string, any>> | undefined;
  }
}

function addDataLayer(parameters: Record<string, string | number | boolean>) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/prefer-nullish-coalescing
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(parameters);
}

/** @deprecated use trackingEvent from now on. delete after transition to GA4 is complete */
export const trackingEventForGA4 = (action = '', category = '', label = '', parameters = {}) => {
  if (!action || !category) return;

  const eventParams = {
    ua_event_action: action,
    ua_event_category: category,
    ua_event_label: label,
    ...parameters,
  };

  // eslint-disable-next-line no-console
  console.log(
    `Logging event for category ${category} and action ${action}${
      label ? ` with label ${label}. it send to GA4` : ''
    }`,
    parameters
  );

  addDataLayer({
    event: action,
    ...eventParams,
  });
};

function trackingEvent(
  eventName: string,
  parameters: Record<string, string | number | boolean> = {}
) {
  if (!eventName) return;

  // eslint-disable-next-line no-console
  console.log(`Logging event for eventName ${eventName} it send to GA4`, parameters);

  addDataLayer({
    event: eventName,
    ...parameters,
  });
}

function setUserProperties(userProperties: Record<string, string | number | boolean> = {}) {
  // eslint-disable-next-line no-console
  console.log(`Logging set user properties`, userProperties);

  addDataLayer({
    set: 'user_properties',
    ...userProperties,
  });
}

export const GA4EventRequestCreation = {
  blogViewed: 'blogViewed',
  proProfileViewed: 'proProfileViewed',
  cd_requestResultViewed: 'cd_requestResultViewed',
  startNewRequestClicked: 'startNewRequestClicked',
  requestIntroViewed: 'requestIntroViewed',
  requestIntroClicked: 'requestIntroClicked',
  q1Viewed: 'q1Viewed',
  q2Viewed: 'q2Viewed',
  q3Viewed: 'q3Viewed',
  q4Viewed: 'q4Viewed',
  q5Viewed: 'q5Viewed',
  q6Viewed: 'q6Viewed',
  q7Viewed: 'q7Viewed',
  q8Viewed: 'q8Viewed',
  q9Viewed: 'q9Viewed',
  addressViewed: 'addressViewed',
  addressClicked: 'addressClicked',
  matchingAnimationViewed: 'matchingAnimationViewed',
  emailViewed: 'emailViewed',
  emailClicked: 'emailClicked',
  nameViewed: 'nameViewed',
  nameClicked: 'nameClicked',
  phoneNumberViewed: 'phoneNumberViewed',
  phoneNumberClicked: 'phoneNumberClicked',
  summaryViewed: 'summaryViewed',
  summaryClicked: 'summaryClicked',
  socialLoginClicked: 'socialLoginClicked',
  socialEmailConfirmationViewed: 'socialEmailConfirmationViewed',
  socialEmailConfirmationClicked: 'socialEmailConfirmationClicked',
  socialUserAuthViewed: 'socialUserAuthViewed',
  socialUserAuthPasswordLink: 'socialUserAuthPasswordLink',
  socialUserAuthClicked: 'socialUserAuthClicked',
  socialUserAuthSuccess: 'socialUserAuthSuccess',
  socialLinkageCreated: 'socialLinkageCreated',
  socialPhoneNumberViewed: 'socialPhoneNumberViewed',
  socialPhoneNumberClicked: 'socialPhoneNumberClicked',
  newRequestSubmitted: 'newRequestSubmitted',
  quitRequestClicked: 'quitRequestClicked',
  quitRequestContinued: 'quitRequestContinued',
  quitRequestConfirmed: 'quitRequestConfirmed',
  quitRequestReasonSent: 'quitRequestReasonSent',
  newRequestCreated: 'newRequestCreated',
} as const;

export type GA4EventRequestCreation =
  typeof GA4EventRequestCreation[keyof typeof GA4EventRequestCreation];

export function trackingEventForRequestCreation(
  eventName: GA4EventRequestCreation | '',
  parameters: {
    C1?: string;
    C2?: string;
    existingUser?: boolean;
    jobTypeId?: string;
    socialType?: string;
    whereFrom?: string;
  } = {}
) {
  trackingEvent(eventName, parameters);
}

export const GA4EventDirectBooking = {
  db_lpViewed: 'db_lpViewed',
  db_startBookingClicked: 'db_startBookingClicked',
  db_locationIdViewed: 'db_locationIdViewed',
  db_locationIdClicked: 'db_locationIdClicked',
  db_coursesViewed: 'db_coursesViewed',
  db_coursesClicked: 'db_coursesClicked',
  db_listOptionsViewed: 'db_listOptionsViewed',
  db_listOptionsClicked: 'db_listOptionsClicked',
  db_dateTimeViewed: 'db_dateTimeViewed',
  db_dateTimeViewed_unavailable: 'db_dateTimeViewed_unavailable',
  db_dateTimeClicked_back: 'db_dateTimeClicked_back',
  db_dateTimeClicked_form: 'db_dateTimeClicked_form',
  db_dateTimeClicked: 'db_dateTimeClicked',
  db_residenceAddressViewed: 'db_residenceAddressViewed',
  db_residenceAddressClicked: 'db_residenceAddressClicked',
  db_pricingSummaryViewed: 'db_pricingSummaryViewed',
  db_pricingSummaryClicked: 'db_pricingSummaryClicked',
  db_personalInfoViewed: 'db_personalInfoViewed',
  db_personalInfoLoginClicked: 'db_personalInfoLoginClicked',
  db_loginViewed: 'db_loginViewed',
  db_loginForgetPasswordClicked: 'db_loginForgetPasswordClicked',
  db_loginClicked: 'db_loginClicked',
  db_personalInfoViewed_afterLogin: 'db_personalInfoViewed_afterLogin',
  db_personalInfoClicked: 'db_personalInfoClicked',
  db_paymentViewed: 'db_paymentViewed',
  db_paymentClicked: 'db_paymentClicked',
  db_bookingCompleted: 'db_bookingCompleted',
  db_bookingCompletedClicked: 'db_bookingCompletedClicked',
  db_dashboardFirstViewed: 'db_dashboardFirstViewed',
} as const;

export type GA4EventDirectBooking =
  typeof GA4EventDirectBooking[keyof typeof GA4EventDirectBooking];

export function trackingEventForDirectBooking(
  eventName: GA4EventDirectBooking | '',
  parameters: {
    whereFrom?: string;
  } = {},
  userProperties?: {
    authenticated: boolean;
  }
) {
  if (userProperties) {
    setUserProperties(userProperties);
  }
  trackingEvent(eventName, parameters);
}

export const GA4EventProRegistration = {
  lp_topViewed: 'lp_topViewed',
  lp_c1Viewed: 'lp_c1Viewed',
  lp_c2Viewed: 'lp_c2Viewed',
  lp_c3Viewed: 'lp_c3Viewed',
  lp_verticalViewed: 'lp_verticalViewed',
  blogViewed: 'blogViewed',
  proProfileViewed: 'proProfileViewed',
  cd_myRequestsViewed: 'cd_myRequestsViewed',
  pro_goProRegistration: 'pro_goProRegistration',
  lp_proViewed: 'lp_proViewed',
  pro_startRegistrationClicked: 'pro_startRegistrationClicked',
  pro_socialLoginClicked: 'pro_socialLoginClicked',
  pro_socialEmailVerifyViewed: 'pro_socialEmailVerifyViewed',
  pro_socialEmailVerifyClicked: 'pro_socialEmailVerifyClicked',
  pro_socialUserAuthViewed: 'pro_socialUserAuthViewed',
  pro_socialUserAuthPasswordLink: 'pro_socialUserAuthPasswordLink',
  pro_socialUserAuthClicked: 'pro_socialUserAuthClicked',
  pro_socialUserAuthSuccess: 'pro_socialUserAuthSuccess',
  pro_selectJobTypeViewed: 'pro_selectJobTypeViewed',
  pro_selectJobTypeSearch: 'pro_selectJobTypeSearch',
  pro_selectJobTypeClicked: 'pro_selectJobTypeClicked',
  pro_relatedJobTypeViewed: 'pro_relatedJobTypeViewed',
  pro_relatedJobTypeAction: 'pro_relatedJobTypeAction',
  pro_relatedJobTypeClicked: 'pro_relatedJobTypeClicked',
  pro_nameViewed: 'pro_nameViewed',
  pro_nameClicked: 'pro_nameClicked',
  pro_addressViewed: 'pro_addressViewed',
  pro_addressClicked: 'pro_addressClicked',
  pro_emailViewed: 'pro_emailViewed',
  pro_emailClicked: 'pro_emailClicked',
  pro_phoneNumberViewed: 'pro_phoneNumberViewed',
  pro_phoneNumberClicked: 'pro_phoneNumberClicked',
  pro_passwordViewed: 'pro_passwordViewed',
  pro_referralViewed: 'pro_referralViewed',
  pro_referralClicked: 'pro_referralClicked',
  pro_basicInfoErrorViewed: 'pro_basicInfoErrorViewed',
  pro_basicInfoErrorLogin: 'pro_basicInfoErrorLogin',
  pro_basicInfoErrorCTA: 'pro_basicInfoErrorCTA',
  pro_quitRegistrationClicked: 'pro_quitRegistrationClicked',
  pro_quitRegistrationContinued: 'pro_quitRegistrationContinued',
  pro_quitRegistrationConfirmed: 'pro_quitRegistrationConfirmed',
  pro_registrationCompleted: 'pro_registrationCompleted',
  pro_registrationCompletedClicked: 'pro_registrationCompletedClicked',
  pro_quitRegistrationDocumentsClicked: 'pro_quitRegistrationDocumentsClicked',
  pro_businessInfoViewed: 'pro_businessInfoViewed',
  pro_businessInfoClicked: 'pro_businessInfoClicked',
  pro_avatarUploadViewed: 'pro_avatarUploadViewed',
  pro_avatarUploadClicked: 'pro_avatarUploadClicked',
  pro_avatarCroppingClicked: 'pro_avatarCroppingClicked',
  pro_avatarChangeClicked: 'pro_avatarChangeClicked',
  pro_avatarCompleteClicked: 'pro_avatarCompleteClicked',
  pro_profileCompleteViewed: 'pro_profileCompleteViewed',
  pro_profileCompleteClicked: 'pro_profileCompleteClicked',
} as const;

export type GA4EventProRegistration =
  typeof GA4EventProRegistration[keyof typeof GA4EventProRegistration];

export function trackingEventForProRegistration(
  eventName: GA4EventProRegistration,
  parameters: {
    C1?: string;
    C2?: string;
    existingUser?: boolean;
    jobTypeId?: string;
    proSignUpTrees?: string;
    socialType?: string;
    whereFrom?: string;
  } = {}
) {
  trackingEvent(eventName, parameters);
}

export const GA4EventWebVital = {
  webVital_FCP: 'webVital_FCP',
  webVital_LCP: 'webVital_LCP',
  webVital_CLS: 'webVital_CLS',
  webVital_FID: 'webVital_FID',
  webVital_TTFB: 'webVital_TTFB',
} as const;

export type GA4EventWebVital = typeof GA4EventWebVital[keyof typeof GA4EventWebVital];

export function trackingEventForWebVital(
  eventName: GA4EventWebVital,
  parameters: {
    effectiveType?: string;
    traceId?: string;
    value?: number;
  } = {}
) {
  trackingEvent(eventName, parameters);
}
